import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import InfoPopup from '~/components/InfoPopup'
import i18n from '~/components/i18n'
import { formatNumber } from '~/utils/helpers'
import { StyledResultsFooter, StyledTableCell } from './style'

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Inventory addition'),
    key: 'inventoryAddition',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
  },
  {
    name: i18n.t('Calf rearing'),
    key: 'calfRearing',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
  },
  {
    name: i18n.t('Feeding'),
    key: 'feeding',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
  },
  {
    name: i18n.t('Manure and Bedding'),
    key: 'manureAndBedding',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
  },
  {
    name: i18n.t('Water and Energy'),
    key: 'waterAndEnergy',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
  },
  {
    name: i18n.t('Diesel Production'),
    key: 'dieselProductionAndCombustion',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]'
  },
  {
    name: i18n.t('GHG emissions per cow per year'),
    key: 'total',
    unit: '[kg CO<sub>2</sub>-Äq./Kuh u. Jahr]',
    info: 'Dieser Wert entspricht der Summe aller berücksichtigten Treibhausgasemissionen.'
  },
  {
    name: i18n.t('GHG emissions per kg of milk sold'),
    key: 'soldMilk',
    type: 'no-unit-number',
    unit: '[kg CO<sub>2</sub>-Äq./kg Milch]',
    info: `Die THG-Emissionen je kg verkaufter Milch entsprechen der Summe aller THG-Emissionen je
    Kuh und Jahr dividiert durch die Gesamtmenge der verkauften Milch in kg.`
  },
  {
    name: i18n.t('GHG emissions per kg of milk sold'),
    key: 'soldMilkFpcm',
    type: 'no-unit-number',
    unit: '[kg CO<sub>2</sub>-Äq./kg Milch (FPCM)]',
    info: `Die THG-Emissionen je kg Milch (FPCM) entsprechen der Summe der THG-Emissionen je Kuh
    und Jahr dividiert durch die Menge an fett- und eiweißkorrigierter, verkaufter Milch in kg.
    Dafür wird ihre Rohmilchmenge nach IDF auf 4% Fett und 3,3% Eiweiß (FPCM) standardisiert.`
  },
  {
    name: i18n.t('GHG emissions with economic allocation per kg of milk sold'),
    key: 'soldMilkEconomicAllocationFpcm',
    type: 'no-unit-number',
    unit: '[kg CO<sub>2</sub>-Äq./kg Milch (FPCM)]',
    info: `Die THG-Emissionen mit ökonomischer Allokation je kg verkaufter Milch (FPCM) entsprechen
    den anteiligen THG-Emissionen, unter Berücksichtigung der Milcherlöse am Gesamterlös, geteilt
    durch die verkaufte, fett- und eiweißkorrigierter Milchmenge.
    Der Betrieb erzeugt neben Milch noch weitere Produkte. Es ist daher verursachergerecht, die
    Gesamtemissionen des Produktionsverfahrens Milchkuhhaltung auf die Produkte aufzuteilen.
    Diese regelbasierte Verteilung wird als Allokation bezeichnet. Die Treibhausgasemissionen
    werden auf die drei Kategorien Milcherlös, Kälbererlös und Altkuherlös aufgeteilt. So wird
    verhindert, dass der Milch die Gesamt-Treibhausgasemissionen angelastet werden, während
    das Kalb zum Beispiel ohne Emissionen zur Kalbinnenaufzucht bzw. zur Bullenmast weiter
    verkauft werden würde.
    Dafür wird zunächst der monetäre Ertrag der drei Produkte Kälber, Altkuh und Milch berechnet.
    Mithilfe des Gesamterlöses findet eine prozentuale Aufteilung statt. Entsprechend dieser
    prozentualen Gewichtung werden die gesamten Treibhausgasemissionen den drei Produkten
    Kälber, Altkuh und Milch zugeteilt. Die Werte zur Berechnung der Erlöse stammen aus den LfL-
    Deckungsbeiträgen und Kalkulationsdaten- Milchkuhhaltung.`
  },
  {
    name: i18n.t('GHG emissions with allocation according to IDF (international comparison value) per kg of milk sold'),
    key: 'soldMilkEconomicAllocationFpcmIdf',
    type: 'no-unit-number',
    unit: '[kg CO<sub>2</sub>-Äq./kg Milch (FPCM)]',
    info: `Die THG-Emissionen mit Allokation nach IDF je kg verkaufter Milch (FPCM) entsprechen den THG-Emissionen je Kuh und Jahr dividiert durch die Menge an fett- und eiweißkorrigierter, verkaufter Milch in kg aufgeteilt auf die Lebendgewicht-Erträge aus dem Verkauf des Kalbes und der Altkuh sowie der verkauften Milchmenge mit der Fett- und Eiweißkorrektur nach der Formel von IDF mit 4 % Fett und 3,3 % Eiweiß.
    Ähnlich der ökonomischen Allokation werden die Treibhausgasemissionen verursachergerecht auf die drei Produkte Kalb, Altkuh und Milch verteilt. Als Verteilungsschlüssel dient laut IDF-Standard die Ertragsmenge in kg. Dafür wird der Kälberertrag in kg Lebendgewicht, der Altkuhertrag in kg Schlachtgewicht und der Milchertrag in kg FPCM berechnet. Der Verteilungsschlüssel (Allokationsfaktor) erfolgt dann auf Grundlage der Ertragsmenge mit Berücksichtigung des jeweiligen Energiebedarfs für die Milch- und Fleischproduktion. 
    Dieser CO2-Fußabdruck dient als internationaler Vergleichswert, da er auf Basis des IDF-Standards ermittelt wurde.
    `,
    className: 'bold-cell'
  }
]

interface Props {
  results: any
  removeMargin?: boolean
}

const Results: React.FC<Props> = ({ results, removeMargin }) => {
  const { t } = useTranslation()
  const tableColumnHeaders = getTableColumnHeaders()
  const { firstColor } = useContext(ThemeContext)

  function renderCellValue(value, row) {
    switch (row.type) {
      case 'no-unit-number':
        return value ? formatNumber(value) : ''
      default:
        return formatNumber(value, 0) || ''
    }
  }

  function getStyleColumn() {
    return {
      background: firstColor,
      color: 'white'
    }
  }

  return results && <>
    <Table style={{ marginTop: removeMargin ? 0 : 80, marginBottom: 15 }} celled compact>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Key figures for greenhouse gas assessment')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Result')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {tableColumnHeaders.map(showDataLabelAndKey => {
          const isTotalColumn = showDataLabelAndKey.key === 'total'
          return <Table.Row key={showDataLabelAndKey.key}>
            <StyledTableCell
              style={isTotalColumn ? getStyleColumn() : {}}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div
                  className={showDataLabelAndKey?.className || ''}
                  dangerouslySetInnerHTML={{ __html: `${showDataLabelAndKey.name} ${showDataLabelAndKey.unit}` }} />
                {showDataLabelAndKey?.info && <InfoPopup
                  content={showDataLabelAndKey.info}
                  wide='very'
                  miWidth={600}
                  styleIcon={isTotalColumn ? { ...getStyleColumn(), fontSize: '1.3em' } : { fontSize: '1.3em' }}
                  position={'top center'}
                />}
              </div>
            </StyledTableCell>
            <StyledTableCell
              style={isTotalColumn ? getStyleColumn() : {}}
            >
              <div className={showDataLabelAndKey?.className || ''}>
                {renderCellValue(results[showDataLabelAndKey.key], showDataLabelAndKey)}
              </div>
            </StyledTableCell>
          </Table.Row>
        })}
      </Table.Body>
    </Table>
    <StyledResultsFooter>
      <div dangerouslySetInnerHTML={{ __html: t('The normal range for the value according to IDF is between 0.8 and 2.0 kg CO2 equivalents/kg milk') }} />
    </StyledResultsFooter>
  </>
}
export default Results